"use client";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ChevronRight, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { motion } from "motion/react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const iconAnimation = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        type: "spring" as const,
        stiffness: 200,
        damping: 10
      }
    }
  };

  return (
    <div className="min-h-[100vh] min-h-[100dvh] flex flex-col bg-linear-to-b from-white via-slate-50 to-blue-50">
      <Navbar initialAuth={{ isAuthenticated: false, userType: null }} />
      
      <main className="flex-1 container max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <h1 className="text-5xl font-bold tracking-tight text-slate-900 sm:text-6xl">
            Simple, Transparent <span className="bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Pricing</span>
          </h1>
          <p className="mt-6 text-xl text-slate-600 max-w-2xl mx-auto">
            Choose the perfect plan for your property search needs with flexible subscription options.
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div 
          className="flex justify-center mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white p-1.5 rounded-full shadow-lg inline-flex">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-8 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
                billingCycle === "monthly"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-slate-600 hover:text-blue-600"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-8 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
                billingCycle === "annual"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-slate-600 hover:text-blue-600"
              }`}
            >
              Annual
              <span className="ml-2 inline-flex items-center py-0.5 px-2 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                Save up to 67%
              </span>
            </button>
          </div>
        </motion.div>

        {/* Featured Cards Container */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Pay-as-You-Go Section */}
          <motion.div variants={fadeInUp}>
            <Card className="shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden h-full bg-white border-slate-200 rounded-2xl">
              <CardHeader className="bg-linear-to-tr from-slate-800 to-slate-900 text-white pb-8">
                <motion.div 
                  className="absolute top-4 right-4"
                  variants={iconAnimation}
                >
                  <Sparkles className="h-6 w-6 text-slate-300" />
                </motion.div>
                <CardTitle className="text-2xl flex items-center">
                  <span className="mr-2">Pay-as-You-Go</span>
                </CardTitle>
                <CardDescription className="text-slate-300">Perfect for occasional searches</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="mb-6">
                  <p className="text-4xl font-bold text-slate-900">₦3,999</p>
                  <p className="text-sm text-slate-500">per review</p>
                </div>
                <motion.ul 
                  className="space-y-3 my-6"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.li className="flex items-center" variants={fadeInUp}>
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 text-slate-700 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Standard Support</span>
                  </motion.li>
                  <motion.li className="flex items-center" variants={fadeInUp}>
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 text-slate-700 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Property Rental Status only</span>
                  </motion.li>
                  <motion.li className="flex items-center" variants={fadeInUp}>
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 text-slate-700 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Rent Price History</span>
                  </motion.li>
                </motion.ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-slate-800 hover:bg-slate-900 flex items-center justify-center group">
                  <span>Get Started</span>
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform transform-gpu group-hover:translate-x-1" />
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Standard Plan */}
          <motion.div variants={fadeInUp}>
            <Card className={cn(
              "shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden h-full bg-white border-slate-200 rounded-2xl",
              billingCycle === "annual" && "ring-2 ring-blue-500"
            )}>
              <CardHeader className="bg-linear-to-tr from-blue-600 to-blue-700 text-white pb-8 relative">
                {billingCycle === "annual" && (
                  <motion.div 
                    className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs px-3 py-1 rounded-bl-md font-semibold"
                    initial={{ x: 100 }}
                    animate={{ x: 0 }}
                    transition={{ type: "spring" as const, stiffness: 100 }}
                  >
                    SAVE 67%
                  </motion.div>
                )}
                <CardTitle className="text-2xl">Standard</CardTitle>
                <CardDescription className="text-blue-100">{billingCycle === "monthly" ? "Monthly" : "Annual"} Plan</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 pb-8">
                <div className="mb-6">
                  <motion.p 
                    className="text-4xl font-bold text-slate-900"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    {billingCycle === "monthly" ? "₦19,999" : "₦79,999"}
                  </motion.p>
                  <p className="text-sm text-slate-500">per {billingCycle === "monthly" ? "month" : "year"}</p>
                </div>
                <ul className="space-y-3 my-6">
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Includes {billingCycle === "monthly" ? "10" : "10 Monthly"} Property Reviews</span>
                  </li>
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">{billingCycle === "monthly" ? "Standard" : "Priority"} Support</span>
                  </li>
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Property {billingCycle === "monthly" ? "Rental Status only" : "Rental & Sale Status"}</span>
                  </li>
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Rent Price History</span>
                  </li>
                  {billingCycle === "annual" && (
                    <>
                      <li className="flex items-start">
                        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3 mt-0.5">
                          <Check className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-slate-800">Custom Alerts:</span>
                          <p className="text-sm text-slate-600 mt-1">Notified when a new review is uploaded for a property you viewed</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3 mt-0.5">
                          <Check className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-slate-800">Anonymous Q&A:</span>
                          <p className="text-sm text-slate-600 mt-1">Ask 1–3 questions per review directly to the reviewer</p>
                        </div>
                      </li>
                    </>
                  )}
                </ul>
              </CardContent>
              <CardFooter>
                                  <Button className="w-full bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] group flex items-center justify-center">
                  <span>Subscribe Now</span>
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform transform-gpu group-hover:translate-x-1" />
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Premium Plan */}
          <motion.div variants={fadeInUp}>
            <Card className={cn(
              "shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden h-full bg-white border-slate-200 rounded-2xl relative",
              billingCycle === "monthly" && "ring-2 ring-indigo-500"
            )}>
              <div className="absolute top-0 right-0 w-40 h-40 translate-x-20 -translate-y-20 bg-indigo-600/10 rounded-full blur-3xl"></div>
              <div className="absolute -top-px left-0 right-0 h-1.5 bg-linear-to-r from-indigo-500 via-violet-500 to-indigo-500"></div>
              <CardHeader className="bg-linear-to-tr from-indigo-600 to-violet-700 text-white pb-8 relative">
                {billingCycle === "annual" && (
                  <motion.div 
                    className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs px-3 py-1 rounded-bl-md font-semibold"
                    initial={{ x: 100 }}
                    animate={{ x: 0 }}
                    transition={{ type: "spring" as const, stiffness: 100 }}
                  >
                    SAVE 53%
                  </motion.div>
                )}
                <div className="absolute top-3 right-3">
                  <motion.div 
                    className="text-xs font-bold px-3 py-1 bg-white text-indigo-600 rounded-full shadow-md flex items-center"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6, type: "spring" as const, stiffness: 200 }}
                  >
                    <Star className="h-3 w-3 mr-1 fill-current" /> POPULAR
                  </motion.div>
                </div>
                <CardTitle className="text-2xl">Premium</CardTitle>
                <CardDescription className="text-indigo-100">{billingCycle === "monthly" ? "Monthly" : "Annual"} Plan</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 pb-8">
                <div className="mb-6">
                  <motion.p 
                    className="text-4xl font-bold text-slate-900"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    {billingCycle === "monthly" ? "₦49,999" : "₦279,999"}
                  </motion.p>
                  <p className="text-sm text-slate-500">per {billingCycle === "monthly" ? "month" : "year"}</p>
                </div>
                <ul className="space-y-3 my-6">
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Includes {billingCycle === "monthly" ? "40" : "40 Monthly"} Property Reviews</span>
                  </li>
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">{billingCycle === "monthly" ? "Standard" : "Priority"} Support</span>
                  </li>
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Property Rental & Sale Status</span>
                  </li>
                  <li className="flex items-center">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-600 mr-3">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-slate-700">Rent Price History</span>
                  </li>
                  {billingCycle === "annual" && (
                    <>
                      <li className="flex items-start">
                        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-600 mr-3 mt-0.5">
                          <Check className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-slate-800">Custom Alerts:</span>
                          <p className="text-sm text-slate-600 mt-1">Notified when a new review is uploaded for a property you viewed</p>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-600 mr-3 mt-0.5">
                          <Check className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="font-semibold text-slate-800">Anonymous Q&A:</span>
                          <p className="text-sm text-slate-600 mt-1">Ask 1–3 questions per review directly to the reviewer</p>
                        </div>
                      </li>
                    </>
                  )}
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 group flex items-center justify-center">
                  <span>Subscribe Now</span>
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform transform-gpu group-hover:translate-x-1" />
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>

        {/* Plan Comparison */}
        <motion.div 
          className="mt-16 bg-white p-8 rounded-2xl shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-center mb-8 text-slate-900">Plan Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="py-4 px-4 text-left font-medium text-slate-500">Features</th>
                  <th className="py-4 px-4 text-center font-medium text-slate-500">Pay-as-You-Go</th>
                  <th className="py-4 px-4 text-center font-medium text-slate-500">Standard</th>
                  <th className="py-4 px-4 text-center font-medium text-slate-500">Premium</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Property Reviews</td>
                  <td className="py-4 px-4 text-center text-slate-700">1 per purchase</td>
                  <td className="py-4 px-4 text-center text-slate-700">10 per month</td>
                  <td className="py-4 px-4 text-center text-slate-700">40 per month</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Support Level</td>
                  <td className="py-4 px-4 text-center text-slate-700">Standard</td>
                  <td className="py-4 px-4 text-center text-slate-700">{billingCycle === "monthly" ? "Standard" : "Priority"}</td>
                  <td className="py-4 px-4 text-center text-slate-700">{billingCycle === "monthly" ? "Standard" : "Priority"}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Property Status</td>
                  <td className="py-4 px-4 text-center text-slate-700">Rental Only</td>
                  <td className="py-4 px-4 text-center text-slate-700">{billingCycle === "monthly" ? "Rental Only" : "Rental & Sale"}</td>
                  <td className="py-4 px-4 text-center text-slate-700">Rental & Sale</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Custom Alerts</td>
                  <td className="py-4 px-4 text-center text-slate-700">—</td>
                  <td className="py-4 px-4 text-center text-slate-700">{billingCycle === "monthly" ? "—" : "✓"}</td>
                  <td className="py-4 px-4 text-center text-slate-700">{billingCycle === "monthly" ? "—" : "✓"}</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-700">Anonymous Q&A</td>
                  <td className="py-4 px-4 text-center text-slate-700">—</td>
                  <td className="py-4 px-4 text-center text-slate-700">{billingCycle === "monthly" ? "—" : "✓"}</td>
                  <td className="py-4 px-4 text-center text-slate-700">{billingCycle === "monthly" ? "—" : "✓"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div 
          className="mt-24 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-slate-900">Frequently Asked Questions</h2>
          <p className="text-slate-600 mb-12 max-w-2xl mx-auto">
            Have questions about our pricing plans? We're here to help you choose the right plan for your needs.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 text-left max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">Can I upgrade or downgrade my plan?</h3>
              <p className="text-slate-600">Yes, you can change your plan at any time. When upgrading, we'll prorate the remaining balance of your current plan toward your new plan.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">Are there any refunds if I cancel?</h3>
              <p className="text-slate-600">We offer a 7-day money-back guarantee for all new subscriptions. After that period, we don't provide refunds for cancellations.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">Do unused property reviews rollover?</h3>
              <p className="text-slate-600">Property reviews do not roll over to the next billing cycle, but you can purchase additional reviews at any time.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">What payment methods do you accept?</h3>
              <p className="text-slate-600">We accept all major credit cards, bank transfers, and mobile payment options available in Nigeria.</p>
            </div>
          </div>
        </motion.div>
        
        {/* CTA Section */}
        <motion.div 
          className="mt-20 text-center bg-linear-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <h2 className="text-3xl font-bold text-white mb-4">Ready to get started?</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of property seekers who make informed decisions with RentWise.
          </p>
          <Button className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 h-auto">
            Sign Up Now
          </Button>
        </motion.div>
      </main>
      
      <Footer />
    </div>
  );
} 